import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { pagesAPI } from '../../services/api';

/**
 * Generic HTML editor for the editable static pages stored in the `pages`
 * collection. Used by Terms of Service and Privacy Policy today.
 *
 * `slug`        — DB slug (e.g. "terms", "privacy")
 * `label`       — human-readable page name shown in headings
 * `publicPath`  — public URL for the "View live" link (e.g. "/terms")
 * `helpText`    — optional guidance shown under the heading
 */
const PageContentAdmin = ({
  slug,
  label,
  publicPath,
  helpText = null,
}) => {
  const [title, setTitle] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [original, setOriginal] = useState({ title: '', content_html: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await pagesAPI.get(slug);
        if (cancelled) return;
        setTitle(d.title || label);
        setContentHtml(d.content_html || '');
        setOriginal({ title: d.title || '', content_html: d.content_html || '' });
        setUpdatedAt(d.updated_at || null);
      } catch {
        if (!cancelled) toast.error(`Failed to load ${label}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, label]);

  const dirty = title !== original.title || contentHtml !== original.content_html;

  const save = async () => {
    setSaving(true);
    try {
      const d = await pagesAPI.adminUpdate(slug, { title, content_html: contentHtml });
      setOriginal({ title: d.title || '', content_html: d.content_html || '' });
      setUpdatedAt(d.updated_at || new Date().toISOString());
      toast.success(`${label} saved`);
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading {label} editor…</div>;

  return (
    <div className="space-y-6" data-testid={`${slug}-admin`}>
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="h-6 w-6 text-blue-600" /> {label}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Edit the public {label} page (rendered at{' '}
          <a
            href={publicPath}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            {publicPath} <ExternalLink className="h-3 w-3" />
          </a>
          ).
        </p>
        {helpText && (
          <p className="text-sm text-gray-600 mt-2">{helpText}</p>
        )}
      </div>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <label htmlFor={`${slug}-title`} className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">
              Page title
            </label>
            <Input
              id={`${slug}-title`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={label}
              data-testid={`${slug}-title-input`}
            />
          </div>

          <div>
            <label htmlFor={`${slug}-body`} className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">
              Content (HTML)
            </label>
            <textarea
              id={`${slug}-body`}
              value={contentHtml}
              onChange={(e) => setContentHtml(e.target.value)}
              rows={28}
              className="w-full font-mono text-xs border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="<h2>Section heading</h2>\n<p>…</p>"
              data-testid={`${slug}-content-textarea`}
            />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="text-xs text-gray-500">
              {updatedAt ? `Last saved · ${new Date(updatedAt).toLocaleString('en-AU')}` : 'Not saved yet'}
              {dirty && <Badge className="ml-2 bg-amber-100 text-amber-700 hover:bg-amber-100">Unsaved</Badge>}
            </div>
            <Button
              className={dirty ? 'bg-blue-600 hover:bg-blue-700' : ''}
              variant={dirty ? 'default' : 'outline'}
              disabled={!dirty || saving}
              onClick={save}
              data-testid={`${slug}-save`}
            >
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PageContentAdmin;
